import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@hortis/ui/breadcrumb";
import { IconButton } from "@hortis/ui/button";
import { Drawer, DrawerContent } from "@hortis/ui/drawer";
import { AlignLeft1, Download1, Plus, XClose } from "@hortis/ui/icons";
import { Tab, TabHeader, TabList } from "@hortis/ui/tab";
import { Link, getRouteApi } from "@tanstack/react-router";
import { useCallback } from "react";
import {
  TopBar,
  TopBarActionButtons,
  TopBarContent,
} from "src/components/nav-layout/topbar";
import { useTopbarStore } from "src/components/nav-layout/topbar/topbar-store";
import { useAccessRole } from "src/features/permissions/use-access-role";
import { getOrgAndSiteFromNamesDocument } from "src/utils/graphql";
import { useGqlQuery } from "src/utils/hooks/graphql";
import { useMobile } from "src/utils/hooks/media-query";
import { twMerge } from "tailwind-merge";

interface MapListOverlayProps {
  open: boolean;
  setDrawerCollapsed: (collapsed: boolean) => void;
  onCreateNewClick?: () => void;
  children: React.ReactNode;
  isContextMenuOpen: boolean;
  tabValue: "materials-list" | "locations-list";
  handleTabChange: (tab: "materials-list" | "locations-list") => void;
  onDownloadRequest?: () => void;
}
const routeApi = getRouteApi("/_layout/site/$siteSlug/map");

function MapListOverlay({
  open,
  setDrawerCollapsed,
  onCreateNewClick,
  children,
  isContextMenuOpen,
  tabValue,
  handleTabChange,
  onDownloadRequest,
}: MapListOverlayProps) {
  const { siteSlug } = routeApi.useParams();
  const isMobile = useMobile();
  const { canEdit } = useAccessRole();
  const setTitle = useTopbarStore((state) => state.setTitle);

  const pageTitle = "Map";
  setTitle(pageTitle);

  const collapseDrawer = useCallback(() => {
    setDrawerCollapsed(true);
  }, [setDrawerCollapsed]);

  const content = (
    <div
      className="flex h-full flex-col bg-white"
      data-cy="material-list-overlay"
    >
      <TopBar className="border-grey-200 px-4 lg:px-6">
        <TopBarContent>
          <Breadcrumb className="hidden lg:flex">
            <BreadcrumbList>
              <BreadcrumbItem>{pageTitle}</BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <Tab value={tabValue} onValueChange={handleTabChange}>
            <TabList>
              <TabHeader asChild value="materials-list">
                <Link to="/site/$siteSlug/map/materials" params={{ siteSlug }}>
                  Materials
                </Link>
              </TabHeader>
              <TabHeader asChild value="locations-list">
                <Link to="/site/$siteSlug/map/locations" params={{ siteSlug }}>
                  Locations
                </Link>
              </TabHeader>
            </TabList>
          </Tab>
        </TopBarContent>
        <TopBarActionButtons>
          {canEdit && (
            <>
              {onDownloadRequest && (
                <IconButton
                  size="sm"
                  icon={Download1}
                  variant="tertiaryGray"
                  ariaLabel="Download CSV"
                  onClick={onDownloadRequest}
                  testId="download-csv-button"
                />
              )}
              <IconButton
                size="sm"
                variant="tertiaryGray"
                onClick={onCreateNewClick}
                ariaLabel={`Create new ${
                  tabValue === "materials-list" ? "material" : "location"
                }`}
                testId={`create-new-${
                  tabValue === "materials-list" ? "material" : "location"
                }`}
                icon={Plus}
              />
            </>
          )}
          <IconButton
            size="sm"
            variant="tertiaryGray"
            onClick={collapseDrawer}
            ariaLabel="Collapse material list"
            testId="collapse-list-button"
            icon={isMobile ? XClose : AlignLeft1}
          />
        </TopBarActionButtons>
      </TopBar>
      {children}
    </div>
  );

  return isMobile ? (
    <Drawer
      key={String(open)}
      open={open}
      onOpenChange={(e) => {
        setDrawerCollapsed(!e);
      }}
      dismissible={false}
    >
      <DrawerContent className="h-[96%] overflow-hidden shadow-md-up">
        {content}
      </DrawerContent>
    </Drawer>
  ) : (
    <div
      className={twMerge(
        "pointer-events-auto z-50 h-full w-full max-w-xs self-center shadow-md lg:max-w-md",
        !open && "hidden",
        !isContextMenuOpen && "pointer-events-auto",
      )}
    >
      {content}
    </div>
  );
}

export { MapListOverlay };
